<template>
  <v-dialog v-model="editForm" width="1200" persistent>
    <v-card>
      <v-card-title class="headline primary"
        ><span class="white--text">Edit NBN Order Details</span></v-card-title
      >
      <ValidationObserver ref="observer" v-slot="{}">
        <v-card-text class="pl-6 pr-6">
          <v-divider class="mt-4 mb-8"></v-divider>
          <v-row>
            <!-- Location ID Display and Edit Button -->
            <v-col cols="12" :md="6" class="pt-0 pb-0">
              <v-text-field
                :value="orderEditformPayload.locationId || nbnOrder.locationId"
                label="Location ID"
                outlined
                dense
                disabled
                readonly
                :placeholder="nbnOrder.locationId"
              ></v-text-field>
            </v-col>
            <v-col cols="12" :md="6" class="pt-0 pb-0">
              <v-text-field
                :value="nbnOrder.productType"
                label="Primary Access Technology"
                outlined
                dense
                disabled
                readonly
                :placeholder="nbnOrder.productType"
              ></v-text-field>
            </v-col>
            <v-col cols="12" :md="8" class="mb-4 pt-1 pb-1">
              <v-btn
                v-if="isUserAllowedToEditLocation"
                @click="openLocationEditor"
                class="primary mt-2 mb-2"
                :disabled="isPendingServiceability"
              >
                Edit Location
              </v-btn>
            </v-col>
            <v-col cols="12" :md="6" class="pt-1 pb-1">
              <ValidationProvider
                name="Customer Firstname"
                :rules="'required'"
                v-slot="{ errors }"
              >
                <v-text-field
                  v-model="orderEditformPayload.customerFirstname"
                  label="Customer Firstname"
                  outlined
                  dense
                  :error-messages="errors"
                  :disabled="isPendingServiceability"
                  class="always-visible-placeholder"
                ></v-text-field>
              </ValidationProvider>
            </v-col>

            <v-col cols="12" :md="6" class="pt-0 pb-1">
              <ValidationProvider
                name="Customer Surname"
                :rules="'required'"
                v-slot="{ errors }"
              >
                <v-text-field
                  v-model="orderEditformPayload.customerSurname"
                  label="Customer Surname"
                  outlined
                  dense
                  :error-messages="errors"
                  :disabled="isPendingServiceability"
                ></v-text-field>
              </ValidationProvider>
            </v-col>

            <v-col cols="12" :md="6" class="pt-0 pb-1">
              <ValidationProvider
                name="Customer Email"
                :rules="'required|email'"
                v-slot="{ errors }"
              >
                <v-text-field
                  v-model="orderEditformPayload.customerContactEmail"
                  label="Customer Email"
                  outlined
                  dense
                  :error-messages="errors"
                  :disabled="isPendingServiceability"
                ></v-text-field>
              </ValidationProvider>
            </v-col>

            <v-col cols="12" :md="6" class="pt-0 pb-1">
              <ValidationProvider
                name="Customer Contact No."
                :rules="'required|custcontactnumber'"
                v-slot="{ errors }"
              >
                <v-text-field
                  v-model="orderEditformPayload.customerContactNumber"
                  label="Customer Contact No."
                  outlined
                  dense
                  :error-messages="errors"
                  :disabled="isPendingServiceability"
                ></v-text-field>
              </ValidationProvider>
            </v-col>

            <v-col v-if="showContractTerm" cols="12" :md="'6'" class="pt-0 pb-1">
              <ValidationProvider
                name="Contract Term"
                :rules="showContractTerm ? 'required' : ''"
                v-slot="{ errors }"
              >
                <v-select
                  ref="contractTerm"
                  :items="CONTRACT_TERMS"
                  label="Contract Term"
                  outlined
                  v-model="orderEditformPayload.contractTerm"
                  :error-messages="errors"
                  dense
                  :disabled="isPendingServiceability"
                ></v-select>
              </ValidationProvider>
            </v-col>

            <v-col cols="12" :md="'6'" class="pt-0 pb-1">
              <ValidationProvider name="Bandwidth Profile" v-slot="{ errors }">
                <v-select
                  ref="getBandwidth"
                  :items="bandwidthProfiles"
                  label="Bandwidth Profile"
                  item-text="text"
                  item-value="value"
                  outlined
                  v-model="orderEditformPayload.bandwidthProfile"
                  :error-messages="errors"
                  dense
                  :disabled="isPendingServiceability"
                >
                  <template v-slot:item="data">
                    <v-list-item
                      v-bind="data.attrs"
                      v-on="data.on"
                      :disabled="data.item.disabled"
                    >
                      <v-list-item-content>
                        <v-list-item-title
                          :class="{
                            'text--disabled': data.item.disabled,
                            'font-italic': data.item.disabled,
                          }"
                        >
                          {{ data.item.text }}
                        </v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </template>
                </v-select>
              </ValidationProvider>
            </v-col>

            <v-col cols="12" :md="'6'" class="pt-0 pb-1" v-if="isLayer3">
              <ValidationProvider name="Radius Username" v-slot="{ errors }">
                <v-text-field
                  v-model="orderEditformPayload.radiusUsername"
                  :placeholder="nbnOrder.radiusUsername ? '' : nbnOrder.existingRadiusUsername"
                  label="Radius Username"
                  outlined
                  dense
                  :error-messages="errors"
                ></v-text-field>
              </ValidationProvider>
            </v-col>
            <v-col cols="12" :md="'6'" class="pt-0 pb-1" v-if="isLayer3">
              <ValidationProvider name="Radius Password" v-slot="{ errors }">
                <v-text-field
                  v-model="orderEditformPayload.radiusPassword"
                  :placeholder="nbnOrder.radiusPassword ? '' : nbnOrder.existingRadiusPassword"
                  label="Radius Password"
                  outlined
                  dense
                  :error-messages="errors"
                ></v-text-field>
              </ValidationProvider>
            </v-col>

            <v-col
              cols="12"
              :md="'6'"
              class="pt-0 pb-1"
              v-if="displayAccountNumberField"
            >
              <ValidationProvider name="Wholesaler Account" v-slot="{ errors }">
                <v-text-field
                  v-model="orderEditformPayload.wholesalerAccountNumber"
                  label="Wholesaler Account Number"
                  :placeholder="nbnOrder.wholesalerAccountNumber"
                  outlined
                  dense
                  :error-messages="errors"
                ></v-text-field>
              </ValidationProvider>
            </v-col>
            <v-col
              cols="12"
              :md="'6'"
              class="pt-0 pb-1"
              v-if="featureFlags.sog7637"
            >
              <ValidationProvider name="Customer Ref." v-slot="{ errors }">
                <v-text-field
                  v-model="orderEditformPayload.customerAuthRef"
                  label="Customer Auth Ref."
                  :placeholder="nbnOrder.customerAuthRef"
                  outlined
                  dense
                  :error-messages="errors"
                ></v-text-field>
              </ValidationProvider>
            </v-col>

            <v-col
              cols="12"
              :md="'6'"
              class="pt-0 pb-1"
              v-if="featureFlags.sog6284 || isAdmin"
            >
              <ValidationProvider name="Network Protocol" v-slot="{ errors }">
                <div
                  style="display: inline-block; font-size: 16px"
                  class="mr-7"
                >
                  Network Protocol
                </div>
                <v-radio-group
                  v-model="orderEditformPayload.isIpoe"
                  row
                  style="display: inline-block"
                  :error-messages="errors"
                  class="mt-0"
                >
                  <v-radio label="IPoE" :value="true" :disabled="!featureFlags.sog5822"></v-radio>
                  <v-radio label="PPPoE" :value="false"></v-radio>
                </v-radio-group>
              </ValidationProvider>
              <span style="display: block; font-size: 14px; font-style: italic"
                >Current type: {{ nbnOrder.isIpoe ? "IPOE" : "PPPoE" }}</span
              >
            </v-col>
          </v-row>
          <v-divider class="mt-4 mb-4"></v-divider>
          <v-row>
            <v-col cols="12" :md="6" class="pt-1 pb-1">
              <div class="text-caption grey--text">Product Template</div>
              <div>{{ productTemplateDisplay || "—" }}</div>
            </v-col>
            <v-col cols="12" :md="6" class="pt-1 pb-1">
              <div class="text-caption grey--text">NTD Id</div>
              <div>{{ orderEditformPayload.ntdId || "—" }}</div>
            </v-col>
            <v-col cols="12" :md="6" class="pt-1 pb-1">
              <div class="text-caption grey--text">NTD Modification</div>
              <div>{{ orderEditformPayload.ntdModifications || "—" }}</div>
            </v-col>
            <v-col cols="12" :md="6" class="pt-1 pb-1">
              <div class="text-caption grey--text">NTD Type</div>
              <div>{{ orderEditformPayload.ntdType || "—" }}</div>
            </v-col>
          </v-row>
        </v-card-text>
      </ValidationObserver>
      <v-divider
        v-if="showSpeedChangeAdminTools"
        class="mt-4 mb-4"
      ></v-divider>
      <v-card-text
        v-if="showSpeedChangeAdminTools"
        class="pt-0 pb-4 pl-6 pr-6"
      >
        <v-alert type="warning" dense outlined class="mb-4">
          Save failed. Use the tools below to diagnose missing NBN order data
          (stale NbnOrderId, externalId, PriId) before retrying.
          <div v-if="lastSaveError" class="mt-2 text-caption">
            {{ lastSaveError }}
          </div>
        </v-alert>
        <div class="d-flex flex-wrap mb-4" style="gap: 8px">
          <v-btn
            small
            outlined
            color="primary"
            :loading="speedChangeToolsLoading"
            @click="runSpeedChangeDiagnose"
          >
            Diagnose speed change
          </v-btn>
          <v-btn
            small
            outlined
            color="primary"
            :loading="speedChangeToolsLoading"
            @click="runSpeedChangeRepair(true)"
          >
            Preview repair
          </v-btn>
          <v-btn
            small
            class="primary"
            :loading="speedChangeToolsLoading"
            @click="runSpeedChangeRepair(false)"
          >
            Apply repair
          </v-btn>
          <v-btn
            small
            class="primary"
            :loading="speedChangeToolsLoading"
            :disabled="!canRetrySpeedChangeSave"
            @click="retrySaveAfterRepair"
          >
            Retry save
          </v-btn>
        </div>
        <v-expansion-panels v-if="speedChangeDiagnostic" flat>
          <v-expansion-panel>
            <v-expansion-panel-header>
              Diagnosis results
              <v-chip
                x-small
                class="ml-2"
                :color="speedChangeDiagnostic.canSpeedChange ? 'success' : 'error'"
                text-color="white"
              >
                {{
                  speedChangeDiagnostic.canSpeedChange
                    ? "Ready"
                    : "Not ready"
                }}
              </v-chip>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <div class="text-caption mb-2">
                Order DB Id: {{ speedChangeDiagnostic.orderId }} |
                NbnOrderId: {{ speedChangeDiagnostic.nbnOrderId || "—" }} |
                PriId: {{ speedChangeDiagnostic.priId || "—" }}
              </div>
              <div
                v-if="hasSpeedChangeIssues"
                class="mb-2"
              >
                <strong>Issues</strong>
                <ul class="pl-4 mb-0">
                  <li
                    v-for="(issue, index) in speedChangeDiagnostic.issues"
                    :key="'issue-' + index"
                  >
                    {{ issue }}
                  </li>
                </ul>
              </div>
              <div
                v-if="hasSpeedChangeWarnings"
                class="mb-2"
              >
                <strong>Warnings</strong>
                <ul class="pl-4 mb-0">
                  <li
                    v-for="(warning, index) in speedChangeDiagnostic.warnings"
                    :key="'warn-' + index"
                  >
                    {{ warning }}
                  </li>
                </ul>
              </div>
              <div v-if="hasSpeedChangeRepairFixes">
                <strong>Repair preview / applied fixes</strong>
                <ul class="pl-4 mb-0">
                  <li
                    v-for="(fix, index) in speedChangeRepairResult.appliedFixes"
                    :key="'fix-' + index"
                  >
                    {{ fix }}
                  </li>
                </ul>
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-card-text>
      <v-divider class="mt-4 mb-8"></v-divider>
      <div class="text-right pr-6 pb-8">
        <v-btn class="primary" :disabled="!isEditFormDirty" @click="saveForm"
          >Save</v-btn
        >
        &nbsp;&nbsp;&nbsp;&nbsp;
        <v-btn class="primary" @click="closeEdit">close</v-btn>
      </div>
    </v-card>
    <v-dialog v-model="showLocationEditor" width="1000" persistent>
      <v-card>
        <v-card-title class="headline primary">
          <span class="white--text">Update Location</span>
        </v-card-title>

        <v-card-text class="pl-6 pr-6">
          <qualification-aggregate-nbn
            ref="qualificationAggregate"
            @qualificationComplete="onQualificationComplete"
          ></qualification-aggregate-nbn>
        </v-card-text>

        <v-divider></v-divider>
        <div class="d-flex justify-end text-right pr-6 pb-6 pt-6">
          <div class="d-flex align-center flex-column">
            <v-btn
              class="primary"
              @click="saveLocationUpdate"
              :disabled="!isMatchingNbnProductType"
            >
              Save Location
            </v-btn>
            <span
              style="color: red; font-size: 10px"
              v-if="newLocationData && !isMatchingNbnProductType"
              >Access Technology does not match
            </span>
          </div>
          &nbsp;&nbsp;&nbsp;&nbsp;
          <v-btn @click="cancelLocationEdit">Cancel</v-btn>
        </div>
      </v-card>
    </v-dialog>
  </v-dialog>
</template>

<script>
import { getNbnProductType } from "../../utils/mappings";
import { DEFAULT_VONEX_ACCOUNT_NUMBER } from "../../constants";
import {
  CONTRACT_TERMS,
  getBandwidthProfilesByProductType,
  getFTTCBandwidthProfiles
} from "../../constants/nbn-edit-form-constants";
import {
  getProductTemplateDisplayText,
  normalizeNullableSelectValue,
  resolveCreateFormSelectDefaults
} from "../../constants/nbn-order-item-constants";
import { wholesalerUsesContractTerm } from "../../utils/wholesaler";

export default {
  name: "nbn-edit-form",
  data: () => ({
    showLocationEditor: false,
    newLocationData: null,
    originalFormValues: {},

    orderEditformPayload: {
      customerFirstname: "",
      customerSurname: "",
      customerContactEmail: "",
      customerContactNumber: "",
      contractTerm: "",
      bandwidthProfile: "",
      radiusUsername: "",
      radiusPassword: "",
      isIpoe: null,
      wholesalerAccountNumber: "",
      locationId: "",
      customerAuthRef: "",
      ntdType: null,
      productTemplate: null,
      ntdId: null,
      ntdModifications: null,
    },
    bandwidthProfiles: [],
    saveFailed: false,
    lastSaveError: "",
    speedChangeDiagnostic: null,
    speedChangeRepairResult: null,
    speedChangeToolsLoading: false,
  }),
  methods: {
    openLocationEditor() {
      this.showLocationEditor = true;
      this.newLocationData = null;
    },

    cancelLocationEdit() {
      this.showLocationEditor = false;
      this.newLocationData = null;

      // Reset the qualification component if needed
      if (this.$refs.qualificationAggregate) {
        this.$refs.qualificationAggregate.clear();
      }
    },

    onQualificationComplete(qualificationData) {
      this.newLocationData = {
        locationId: qualificationData.locationId,
        address: qualificationData.sqResult?.address?.formattedAddress || "",
        serviceClass: qualificationData.serviceClass,
        accessTechnology: qualificationData.accessTechnology,
        serviceabilityStatus: qualificationData.serviceabilityStatus,
        isServiceable: qualificationData.isServiceable,
        csaId: qualificationData.csaId,
        sqResult: qualificationData.sqResult,
      };
    },

    async saveLocationUpdate() {
      if (!this.newLocationData) {
        return;
      }

      try {
        this.orderEditformPayload.locationId = this.newLocationData.locationId;
        this.orderEditformPayload.sqResult = this.newLocationData.sqResult;
        this.orderEditformPayload.serviceClass =
          this.newLocationData.serviceClass;
        this.orderEditformPayload.address = this.newLocationData.address;
        // You might want to update other fields based on the new location
        // For example, if the service qualification affects available bandwidth profiles
        this.showLocationEditor = false;

        // Update original values to reflect the location change
        this.originalFormValues.locationId = this.newLocationData.locationId;

        // Show success message or update UI to reflect the change
        this.$emit("locationUpdated", this.newLocationData);
      } catch (error) {
        // Handle error silently or emit error event
        this.$emit("locationUpdateError", error);
      }
    },

    closeEdit() {
      let vm = this;
      vm.resetSpeedChangeAdminState();
      vm.resetForm();
      vm.showLocationEditor = false;
      vm.newLocationData = null;
      vm.setEditForm(false);
      vm.$emit("closeDialog");
    },
    resetSpeedChangeAdminState() {
      this.saveFailed = false;
      this.lastSaveError = "";
      this.speedChangeDiagnostic = null;
      this.speedChangeRepairResult = null;
      this.speedChangeToolsLoading = false;
    },
    async runSpeedChangeDiagnose() {
      const vm = this;
      vm.speedChangeToolsLoading = true;
      try {
        vm.speedChangeDiagnostic = await vm.diagnoseNbnOrderSpeedChangeAsync(
          vm.nbnOrder.id
        );
      } finally {
        vm.speedChangeToolsLoading = false;
      }
    },
    async runSpeedChangeRepair(dryRun) {
      const vm = this;
      vm.speedChangeToolsLoading = true;
      try {
        vm.speedChangeRepairResult = await vm.repairNbnOrderSpeedChangeAsync({
          orderId: vm.nbnOrder.id,
          dryRun,
        });
        if (vm.speedChangeRepairResult?.postRepairDiagnostic) {
          vm.speedChangeDiagnostic =
            vm.speedChangeRepairResult.postRepairDiagnostic;
        }
      } finally {
        vm.speedChangeToolsLoading = false;
      }
    },
    retrySaveAfterRepair() {
      this.saveFailed = false;
      this.saveForm();
    },
    saveForm() {
      let vm = this;
      const observer = vm.$refs.observer;
      if (observer && typeof observer.validate === "function") {
        return observer.validate().then((valid) => {
          if (!valid) return;
          return vm.submitEditForm();
        });
      }
      return vm.submitEditForm();
    },
    submitEditForm() {
      let vm = this;
      if (vm.orderEditformPayload.isIpoe == null) {
        vm.orderEditformPayload.isIpoe = vm.nbnOrder.isIpoe;
      }
      let payload = vm.orderEditformPayload;
      payload.id = vm.nbnOrder.id;

      if (!vm.showContractTerm) {
        payload.contractTerm = null;
      }

      payload.ntdType = normalizeNullableSelectValue(payload.ntdType);
      payload.productTemplate = normalizeNullableSelectValue(
        payload.productTemplate
      );
      payload.ntdId = normalizeNullableSelectValue(payload.ntdId);
      payload.ntdModifications = normalizeNullableSelectValue(
        payload.ntdModifications
      );

      // Include location ID if it was updated
      if (vm.orderEditformPayload.locationId) {
        payload.locationId = vm.orderEditformPayload.locationId;
      }
      vm.updateNbnOrderAsync(payload)
        .then(() => {
          vm.resetSpeedChangeAdminState();
          vm.$emit("closeForm");
          vm.resetForm();
          vm.setEditForm(false);
        })
        .catch((e) => {
          vm.lastSaveError =
            (e && e.response && e.response.data) || "Save failed.";
          vm.saveFailed = true;
        });
    },
    resetForm() {
      let vm = this;
      vm.orderEditformPayload = {
        customerFirstname: "",
        customerSurname: "",
        customerContactEmail: "",
        customerContactNumber: "",
        contractTerm: "",
        bandwidthProfile: "",
        radiusUsername: "",
        radiusPassword: "",
        isIpoe: null,
        wholesalerAccountNumber: "",
        locationId: "",
        customerAuthRef: "",
        ntdType: null,
        productTemplate: null,
        ntdId: null,
        ntdModifications: null,
      };
      vm.newLocationData = null;
      vm.originalFormValues = {};
      vm.resetSpeedChangeAdminState();
    },
    getTextByValue(value, list) {
      var text = "";
      list.forEach((x) => {
        if (x.value == value) {
          text = x.text;
        }
      });
      return text;
    },
    getSupportingResources() {
      return (
        this.sqNbnResult?.sq?.siteRestriction?.supportingResource || []
      );
    },
    getServiceClass() {
      return (
        this.sqNbnResult?.sq?.siteRestriction?.supportingTechnology
          ?.serviceabilityClass ||
        this.nbnOrder.serviceClass ||
        ""
      );
    },
    getPrimaryAccessTechnology() {
      return (
        this.sqNbnResult?.sq?.siteRestriction?.supportingTechnology
          ?.primaryAccessTechnology || ""
      );
    },
    applyCreateFormDefaults() {
      const defaults = resolveCreateFormSelectDefaults({
        persistedNtdId: this.nbnOrder.ntdId,
        persistedNtdModifications: this.nbnOrder.ntdModifications,
        persistedNtdType: this.nbnOrder.ntdType,
        persistedProductTemplate: this.nbnOrder.productTemplate,
        productType: this.nbnOrder.productType,
        orderType: this.nbnOrder.orderType,
        isFibreConnect: this.nbnOrder.isFibreConnect,
        isOriginal: this.isOriginal,
        featureFlags: this.featureFlags || {},
        supportingResources: this.getSupportingResources(),
        serviceClass: this.getServiceClass(),
        primaryAccessTechnology: this.getPrimaryAccessTechnology()
      });
      this.orderEditformPayload.ntdId = defaults.ntdId;
      this.orderEditformPayload.ntdModifications = defaults.ntdModifications;
      this.orderEditformPayload.ntdType = defaults.ntdType;
      this.orderEditformPayload.productTemplate = defaults.productTemplate;
    },
    getBandwidth() {
      let vm = this;
      var productType = vm.nbnOrder.productType;

      if (productType === "FTTC") {
        return vm.FTTCBandwidthProfile;
      }

      return getBandwidthProfilesByProductType(productType);
    },
    resetBandwidthOption([...bandwidthOptions]) {
      bandwidthOptions.forEach(x => {
        x.disabled = false;
      });
      return bandwidthOptions;
    },
    filterBandwidthOptions(options) {
      let vm = this;
      let orderBandwidth = vm.nbnOrder.bandwidthProfile;

      let filtered = options.filter(x => {
        if (x.obsolete && x.value !== orderBandwidth) return false;
        return true;
      });

      const seen = new Map();
      const result = [];

      for (const opt of filtered) {
        if (!seen.has(opt.text)) {
          seen.set(opt.text, result.length);
          result.push(opt);
        } else if (opt.value === orderBandwidth) {
          result[seen.get(opt.text)] = opt;
        }
      }

      return result;
    },
    getBandwidthOptions() {
      let vm = this;
      let bandwidthOptions = [];
      let productType = vm.nbnOrder.productType;

      switch (productType) {
        case "FTTP":
        case "Fibre":
          bandwidthOptions = vm.getFTTPBandwidthOptions();
          break;

        case "HFC":
          bandwidthOptions = vm.getHFCBandwidthOptions();
          break;

        default:
          bandwidthOptions = vm.getOtherBandwidthOptions() || [];
          break;
      }

      vm.bandwidthProfiles = vm.filterBandwidthOptions(bandwidthOptions);
    },
    getOtherBandwidthOptions() {
      let vm = this;
      let bandwidthOptions = vm.resetBandwidthOption(vm.getBandwidth()) || [];
      let orderBandwidth = vm.nbnOrder.bandwidthProfile;

      bandwidthOptions.forEach(x => {
        x.disabled = (x.value === orderBandwidth);
      });

      return bandwidthOptions;
    },
    getHFCBandwidthOptions() {
      let vm = this;
      let bandwidthOptions = vm.resetBandwidthOption(vm.getBandwidth()) || [];
      let orderBandwidth = vm.nbnOrder.bandwidthProfile;

      const supportingResource =
        vm.sqNbnResult?.sq?.siteRestriction?.supportingResource || [];

      const selectedNTD = supportingResource.find(x => x.id === vm.nbnOrder.ntdId);

      const speedTiersSupported = selectedNTD?.speedTiersSupported;

      if (!Array.isArray(speedTiersSupported) || speedTiersSupported.length === 0) {
        return bandwidthOptions;
      }

      const supportedTierSet = new Set(speedTiersSupported
        .filter(x => x?.supported === true)
        .map(x => String(x.featureType).trim()));

      const tierValues = new Set(["Home Fast", "Home Superfast", "Home Ultrafast", "Home Hyperfast"]);

      return bandwidthOptions.filter(x => {
        if (x.value === orderBandwidth) {
          x.disabled = true;
          return true;
        }
        const value = String(x?.value ?? "").trim();

        if (!tierValues.has(value)) return true;

        return supportedTierSet.has(value);
      });

    },
    getFTTPBandwidthOptions() {
      let vm = this;
      let bandwidthOptions = vm.resetBandwidthOption(vm.getBandwidth()) || [];
      let remainingCapacity = 0;
      let orderBandwidth = vm.nbnOrder.bandwidthProfile;

      if(vm.featureFlags?.sog8809) {
        const resource = vm.sqNbnResult?.sq?.siteRestriction?.supportingResource;

        if (Array.isArray(resource) && resource.length > 0) {

          const selectedNtd = resource.find(x => x?.id === vm.nbnOrder.ntdId);

          if (!selectedNtd) {
            return bandwidthOptions.map(item => ({
              ...item,
              disabled: item.disabled || item.value === orderBandwidth
            }));
          }
          
          const tiers = selectedNtd?.speedTiersSupported;

          if (Array.isArray(tiers) && tiers.length > 0) {
            const allTierNames = tiers.map(t => t.featureType);
            const supportedTierNames = tiers.filter(t => t.supported).map(t => t.featureType);
            
            bandwidthOptions = bandwidthOptions.map(item => {
              const isCurrentBandwidth = item.value === orderBandwidth;
              const isNamedTier = allTierNames.includes(item.value);
              const isSupported = supportedTierNames.includes(item.value);
                  
              return {
                ...item,
                disabled:
                  item.disabled ||
                  isCurrentBandwidth ||
                  (isNamedTier && !isSupported)
              };
            });
          }
        }
      } else{
        const supportingResource = vm.sqNbnResult?.sq?.siteRestriction?.supportingResource || [];

        const selectedNTD = supportingResource.find(x => x.id === vm.nbnOrder.ntdId);

        if (!selectedNTD?.ntdDetails?.length) {
          return vm.getOtherBandwidthOptions();
        }

        const remainingCap = selectedNTD.ntdDetails.find(
          x => x?.name === "Remaining Capacity Downstream"
        );

        if (!remainingCap) {
          return vm.getOtherBandwidthOptions();
        }

        remainingCapacity = Number(remainingCap?.rawValue ?? 0) || 0;

        const getDownstream = (text) => {
          const speed = String(text).match(/^\s*(\d+(?:\.\d+)?)\s*\//);
          return speed ? parseFloat(speed[1]) : NaN;
        }

        bandwidthOptions = bandwidthOptions.filter((x) => {
          if (x.value === orderBandwidth) {
            x.disabled = true;
            return true;
          }
          const downstream = getDownstream(x.text);

          if (!Number.isFinite(downstream)) return true;

          return downstream <= remainingCapacity;
        });
      }

      return bandwidthOptions;
    },
  },
  computed: {
    showSpeedChangeAdminTools() {
      return this.saveFailed && this.isAdmin;
    },
    canRetrySpeedChangeSave() {
      return !!(
        this.speedChangeDiagnostic && this.speedChangeDiagnostic.canSpeedChange
      );
    },
    hasSpeedChangeIssues() {
      return !!(
        this.speedChangeDiagnostic &&
        this.speedChangeDiagnostic.issues &&
        this.speedChangeDiagnostic.issues.length
      );
    },
    hasSpeedChangeWarnings() {
      return !!(
        this.speedChangeDiagnostic &&
        this.speedChangeDiagnostic.warnings &&
        this.speedChangeDiagnostic.warnings.length
      );
    },
    hasSpeedChangeRepairFixes() {
      return !!(
        this.speedChangeRepairResult &&
        this.speedChangeRepairResult.appliedFixes &&
        this.speedChangeRepairResult.appliedFixes.length
      );
    },
    showContractTerm() {
      if (this.nbnOrder.productType === "Enterprise Ethernet") {
        return false;
      }
      const accountNumber =
        this.orderEditformPayload.wholesalerAccountNumber ||
        this.nbnOrder.wholesalerAccountNumber;
      return wholesalerUsesContractTerm(this.wholesalers, accountNumber);
    },
    isUserAllowedToEditLocation() {
      const vm = this;
      const foundAccountNumber = vm.wholesalers.find(
        (x) => x.accountNumber === DEFAULT_VONEX_ACCOUNT_NUMBER
      );
      if (
        vm.isAdmin ||
        (vm.isOriginal && vm.addressResults.length < 2) ||
        foundAccountNumber
      ) {
        return true;
      }
      return false;
    },
    isMatchingNbnProductType() {
      let vm = this;

      if (vm.newLocationData == null) return false;
      let productType = vm.nbnOrder.productType;
      let newLocationAccessTechnology = getNbnProductType(
        vm.newLocationData?.accessTechnology
      );

      return newLocationAccessTechnology === productType;
    },
    getContractPlaceHolder() {
      let vm = this;
      let result = vm.getTextByValue(vm.nbnOrder.contractTerm, CONTRACT_TERMS);
      return result;
    },
    FTTCBandwidthProfile() {
      let vm = this;
      return getFTTCBandwidthProfiles(vm.featureFlags.sog6028);
    },
    productTemplateDisplay() {
      return getProductTemplateDisplayText(
        this.orderEditformPayload.productTemplate,
        this.nbnOrder.productType,
        this.featureFlags,
        this.isOriginal
      );
    },
    displayAccountNumberField() {
      let vm = this;
      let display = false;
      let isAdminImpersonating = false;

      if (vm.impersonating !== null) {
        isAdminImpersonating =
          vm.impersonating.impersonateEmail !== vm.impersonating.loginEmail;
      }

      if (isAdminImpersonating) {
        display = true;
      } else {
        display = vm.isAdmin;
      }

      return display;
    },
    isEditFormDirty() {
      let vm = this;
      if (
        !vm.originalFormValues ||
        Object.keys(vm.originalFormValues).length === 0
      ) {
        return false;
      }

      // Check if any field has changed
      const currentValues = vm.orderEditformPayload;
      const originalValues = vm.originalFormValues;

      // Compare each field
      for (let key in currentValues) {
        if (currentValues.hasOwnProperty(key)) {
          // Handle null/undefined comparison and normalize values
          let currentVal = currentValues[key];
          let originalVal = originalValues[key];

          // Convert both to strings for comparison to handle type mismatches
          currentVal =
            currentVal === null || currentVal === undefined
              ? ""
              : String(currentVal);
          originalVal =
            originalVal === null || originalVal === undefined
              ? ""
              : String(originalVal);

          // Trim whitespace for string comparison
          currentVal = currentVal.trim();
          originalVal = originalVal.trim();

          if (currentVal !== originalVal) {
            // Debug logging - remove this in production
            // console.log(`Field ${key} changed:`, {
            //   current: currentVal,
            //   original: originalVal,
            //   currentType: typeof currentValues[key],
            //   originalType: typeof originalValues[key],
            // });
            return true;
          }
        }
      }

      // Check if location was updated
      if (
        vm.newLocationData &&
        vm.newLocationData.locationId !== vm.nbnOrder.locationId
      ) {
        return true;
      }

      return false;
    },
  },
  watch: {
    showContractTerm(show) {
      if (!show) {
        this.orderEditformPayload.contractTerm = null;
      }
    },
    editForm: function (val) {
      let vm = this;
      if (val) {
        vm.getBandwidthOptions();
        vm.orderEditformPayload.customerFirstname =
          vm.nbnOrder.customerFirstname;
        vm.orderEditformPayload.customerSurname = vm.nbnOrder.customerSurname;
        vm.orderEditformPayload.customerContactEmail =
          vm.nbnOrder.customerContactEmail;
        vm.orderEditformPayload.customerContactNumber =
          vm.nbnOrder.customerContactNumber;
        vm.orderEditformPayload.contractTerm = vm.nbnOrder.contractTerm;
        vm.orderEditformPayload.bandwidthProfile = vm.nbnOrder.bandwidthProfile;
        vm.orderEditformPayload.radiusUsername = vm.nbnOrder.radiusUsername;
        vm.orderEditformPayload.radiusPassword = vm.nbnOrder.radiusPassword;
        vm.orderEditformPayload.isIpoe = vm.nbnOrder.isIpoe;
        vm.orderEditformPayload.customerAuthRef = vm.nbnOrder.customerAuthRef;
        vm.orderEditformPayload.wholesalerAccountNumber =
          vm.nbnOrder.wholesalerAccountNumber;
        vm.orderEditformPayload.locationId = vm.nbnOrder.locationId;
        vm.applyCreateFormDefaults();

        // Store original values for dirty checking
        vm.originalFormValues = JSON.parse(
          JSON.stringify(vm.orderEditformPayload)
        );
        vm.resetSpeedChangeAdminState();
      } else {
        vm.resetForm();
      }
    },
  },
};
</script>

<style scoped></style>
